'use client';

import { useRouter } from 'next/navigation';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  alpha,
  Box,
  CircularProgress,
  type Theme,
  Typography,
  useTheme as useMuiTheme,
} from '@mui/material';

import { useT } from '@/hooks';
import type { AppNotification } from '@/services/api/notification.api';
import { useMarkNotificationsRead } from '@/services/query';
import { formatRelativeTime } from '@/utils';

import { ROUTES } from '@/constants';

const typeIcon = (type: string) => {
  switch (type) {
    case 'referral_reward':
      return <GroupsIcon sx={{ fontSize: 18 }} />;
    case 'price_alert':
      return <NotificationsActiveIcon sx={{ fontSize: 18 }} />;
    case 'streak_milestone':
    case 'streak_at_risk':
      return <LocalFireDepartmentIcon sx={{ fontSize: 18 }} />;
    case 'trade':
    default:
      return <SwapHorizIcon sx={{ fontSize: 18 }} />;
  }
};

const typeColor = (theme: Theme) => ({
  color: theme.palette.primary.main,
  bg: alpha(theme.palette.primary.main, 0.1),
});

/** Horizontal padding, so hosts can align rows with their own gutter. */
type RowPadding = number | { xs?: number; sm?: number; md?: number };

interface NotificationListProps {
  notifications: AppNotification[];
  isLoading?: boolean;
  /** Runs after a row has navigated — lets the header panel close itself. */
  onNavigate?: () => void;
  rowPx?: RowPadding;
  /** Vertical room for the empty state; the header panel wants more than a card. */
  emptyPy?: number;
}

/**
 * The notification feed itself — rows, loading and empty states, and the
 * read-then-navigate behaviour on click. Shared by the header bell's panel and
 * the portfolio dashboard's notification history card so the two cannot drift
 * apart. Scrolling and surface chrome belong to the host.
 */
export const NotificationList = ({
  notifications,
  isLoading = false,
  onNavigate,
  rowPx = 2,
  emptyPy = 6,
}: NotificationListProps) => {
  const { t } = useT();
  const router = useRouter();
  const muiTheme = useMuiTheme();
  const markRead = useMarkNotificationsRead();

  const handleClick = (n: AppNotification) => {
    if (!n.isRead) markRead.mutate([n.id]);
    const slug = n.data?.marketSlug as string | undefined;
    if (n.type === 'referral_reward') {
      router.push(ROUTES.REFERRALS);
    } else if (slug) {
      router.push(ROUTES.MARKET_DETAILS(slug));
    }
    onNavigate?.();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (notifications.length === 0) {
    return (
      <Box sx={{ py: emptyPy, textAlign: 'center', px: rowPx }}>
        <NotificationsNoneIcon
          sx={{ fontSize: 34, color: 'text.secondary', opacity: 0.5, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {t('notifications.empty', { defaultValue: "You're all caught up." })}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {notifications.map((n) => {
        const { color, bg } = typeColor(muiTheme);
        return (
          <Box
            key={n.id}
            role="button"
            tabIndex={0}
            onClick={() => handleClick(n)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(n);
              }
            }}
            sx={{
              display: 'flex',
              gap: 1.5,
              px: rowPx,
              py: 1.5,
              cursor: 'pointer',
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: n.isRead ? 'transparent' : 'action.hover',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'action.selected' },
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                bgcolor: bg,
              }}
            >
              {typeIcon(n.type)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                {n.title}
              </Typography>
              {n.body && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block' }}
                  noWrap
                >
                  {n.body}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {formatRelativeTime(n.createdAt)}
              </Typography>
            </Box>
            {!n.isRead && (
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: 'center',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
            )}
          </Box>
        );
      })}
    </>
  );
};
